export const Track = {
  APPLICATION_SECURITY: "APPLICATION_SECURITY",
  ZERO_TRUST: "ZERO_TRUST",
} as const;

export type Track = (typeof Track)[keyof typeof Track];

export const Domain = {
  APP_FOUNDATIONS: "APP_FOUNDATIONS",
  ACCOUNT_DEFAULT_SECURITY: "ACCOUNT_DEFAULT_SECURITY",
  DNS_TLS: "DNS_TLS",
  WAF_MANAGED_RULES: "WAF_MANAGED_RULES",
  CUSTOM_RULES_LISTS: "CUSTOM_RULES_LISTS",
  RATE_LIMITING_DDOS: "RATE_LIMITING_DDOS",
  API_BOT_SECURITY: "API_BOT_SECURITY",
  ANALYTICS_TROUBLESHOOTING: "ANALYTICS_TROUBLESHOOTING",
  ZERO_TRUST_FOUNDATIONS: "ZERO_TRUST_FOUNDATIONS",
  IDENTITY_ENROLLMENT: "IDENTITY_ENROLLMENT",
  CLIENT_DEVICE_POSTURE: "CLIENT_DEVICE_POSTURE",
  TUNNELS_MESH_ROUTING: "TUNNELS_MESH_ROUTING",
  ACCESS_POLICIES: "ACCESS_POLICIES",
  GATEWAY_POLICIES: "GATEWAY_POLICIES",
  TLS_DLP_CASB_ISOLATION: "TLS_DLP_CASB_ISOLATION",
  ZERO_TRUST_OPERATIONS: "ZERO_TRUST_OPERATIONS",
} as const;

export type Domain = (typeof Domain)[keyof typeof Domain];

export const Difficulty = {
  FOUNDATIONAL: "FOUNDATIONAL",
  APPLIED: "APPLIED",
  SCENARIO: "SCENARIO",
} as const;

export type Difficulty = (typeof Difficulty)[keyof typeof Difficulty];

export const ExamMode = {
  COMPREHENSIVE: "COMPREHENSIVE",
  APPLICATION_SECURITY: "APPLICATION_SECURITY",
  ZERO_TRUST: "ZERO_TRUST",
  FOCUS: "FOCUS",
} as const;

export type ExamMode = (typeof ExamMode)[keyof typeof ExamMode];

export const ViewState = {
  DASHBOARD: "DASHBOARD",
  STUDY: "STUDY",
  LABS: "LABS",
  LOADING: "LOADING",
  EXAM: "EXAM",
  RESULTS: "RESULTS",
  ERROR: "ERROR",
} as const;

export type ViewState = (typeof ViewState)[keyof typeof ViewState];

export const MasteryState = {
  NOT_ASSESSED: "NOT_ASSESSED",
  FOUNDATIONAL: "FOUNDATIONAL",
  DEVELOPING: "DEVELOPING",
  PROFICIENT: "PROFICIENT",
  READY: "READY",
} as const;

export type MasteryState = (typeof MasteryState)[keyof typeof MasteryState];

export const AnswerOutcome = {
  CORRECT: "CORRECT",
  INCORRECT: "INCORRECT",
  UNANSWERED: "UNANSWERED",
} as const;

export type AnswerOutcome = (typeof AnswerOutcome)[keyof typeof AnswerOutcome];

export interface Choice {
  readonly id: string;
  readonly label: string;
}

export interface SourceReference {
  readonly title: string;
  readonly url: string;
}

export interface Question {
  readonly id: string;
  readonly track: Track;
  readonly domain: Domain;
  readonly difficulty: Difficulty;
  readonly objective: string;
  readonly stem: string;
  readonly choices: readonly Choice[];
  readonly correctChoiceId: string;
  readonly explanation: string;
  readonly source: SourceReference;
}

export interface QuestionChunk {
  readonly questions: readonly Question[];
}

export type ResponseMap = Readonly<Record<string, string>>;

export interface QuestionResult {
  readonly questionId: string;
  readonly track: Track;
  readonly domain: Domain;
  readonly selectedChoiceId: string | null;
  readonly correctChoiceId: string;
  readonly outcome: AnswerOutcome;
}

export interface DomainScore {
  readonly domain: Domain;
  readonly correct: number;
  readonly total: number;
  readonly percentage: number;
  readonly mastery: MasteryState;
}

export interface ScoreReport {
  readonly correct: number;
  readonly total: number;
  readonly percentage: number;
  readonly mastery: MasteryState;
  readonly domainScores: readonly DomainScore[];
  readonly results: readonly QuestionResult[];
}

export const ProgressEventType = {
  ATTEMPT_COMPLETED: "ATTEMPT_COMPLETED",
} as const;

export type ProgressEventType =
  (typeof ProgressEventType)[keyof typeof ProgressEventType];

export interface AttemptCompletedEvent {
  readonly schemaVersion: 1;
  readonly eventType: typeof ProgressEventType.ATTEMPT_COMPLETED;
  readonly eventId: string;
  readonly attemptId: string;
  readonly traceId: string;
  readonly occurredAt: string;
  readonly mode: ExamMode;
  readonly questionBankVersion: string;
  readonly report: ScoreReport;
}

export type ProgressEvent = AttemptCompletedEvent;

export interface DomainProgress {
  readonly domain: Domain;
  readonly correct: number;
  readonly total: number;
  readonly percentage: number;
  readonly mastery: MasteryState;
}

export interface ProgressSummary {
  readonly attemptCount: number;
  readonly latestPercentage: number | null;
  readonly overallPercentage: number | null;
  readonly overallMastery: MasteryState;
  readonly domains: readonly DomainProgress[];
}
